import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { useFetch, useFilter, useLanguage, usePagination } from "hooks";
import {
  GridList,
  Layout,
  SearchForm,
  TabSlider,
  MultimediaCard,
  Skeletons,
  SideFilter,
  Icons,
} from "components";
import { STORAGE_URL } from "constants";
import moment from "moment";

const MagazineLinkCard = ({ item }) => {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";

  return (
    <div
      key={item.id}
      className="multimedia_card position-relative border border-1 border-light3 pt-5 rounded-4"
    >
      <div className="px-4 mb-3">
        <div className="d-flex justify-content-center mb-3">
          <div className="img-container d-flex align-items-center justify-content-center">
            <img
              src={`${STORAGE_URL}${item.cover_image}`}
              title="figure"
              alt={item.edition_no}
            />
          </div>
        </div>

        <h5 className="fw-bold text-black cut-text-2 h6 description">
          {language.editionNo}: {item.edition_no}
        </h5>
        <p className="fw-normal text-light4 cut-text-1 ">
          {language.date}:{moment(item.publishing_date).format("YYYY")}
        </p>
      </div>

      <Link
        to={`/book/${item?.book_details?.id}/${item?.id}`}
        className="position-absolute end-0 start-0 bottom-0 btn p-0"
      >
        <div className="view_button user-select-none px-4 py-2 bg-light2">
          <Row className="align-items-center">
            <Col xl={10} lg={10} md={10} sm={10} xs={10}>
              <h6 className="fw-bold">{language.showtheBook}</h6>
            </Col>
            <Col xl={2} lg={2} md={2} sm={2} xs={2}>
              <Icons.ArrowLeft />
            </Col>
          </Row>
        </div>
      </Link>
    </div>
  );
}; // display edition as a card

/*
this page have tow views
if magazines tab is selected it gonna show the magazines but if you search the search results will not be the magazines but the magazines editions
if Research And Studies tab is selected it gonna show the Research And Studies with from api fitter
 */

export default function List() {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const Tabs = [
    {
      type: 2,
      title: language.magazinesSide,
    },
    {
      type: 3,
      title: language.searchStudies,
    },
  ]; // pages tabs magazines, Research And Studies
  const [currentTab, setCurrentTab] = React.useState(1);
  const tab = React.useRef(Tabs[currentTab - 1].type);
  const { page, handleSetPage } = usePagination();
  const [booksResponse, booksRequest] = useFetch(`getBooks`, "POST");
  const [magazinesResponse, magazinesRequest] = useFetch(
    `getSearchForAlDawriaat`,
    "POST"
  );
  const [booksFilterResponse, booksFilterRequest] = useFetch(
    `getBooksFilters`,
    "POST"
  );
  const filters = useFilter(booksFilterResponse.result.data);
  const searchKey = React.useRef("");

  const handleSetTab = (val) => {
    handleSetPage(1);
    setCurrentTab(val);
    filters.reset();
    tab.current = Tabs[val - 1].type;
  };

  const handleSearch = async (keyword) => {
    searchKey.current = keyword;
    handleSetPage(1);
    let filtersToSend = {};
    filters.selectedFilters.forEach((sf1) => {
      filtersToSend[sf1.key] = filters.selectedFilters
        .filter((sf2) => sf1.key === sf2.key)
        .map((sf2) => sf2.values)[0];
    });

    if (tab.current === 3 || !keyword) {
      booksRequest(
        {
          filters: { ...filtersToSend },
          search_key: keyword,
          section_type: tab.current,
        },
        `?page=1`
      );
      booksFilterRequest({
        page_type: "books",
        filters: { ...filtersToSend },
        parent_ids: [],
        section_ids: [3],
        refresh_total: "1",
      });
    } else {
      magazinesRequest(
        {
          filters: { ...filtersToSend },
          search_key: keyword,
          section_type: tab.current,
        },
        `?page=1`
      );
    }
  };

  const handleGetData = React.useCallback(
    async (keyword) => {
      let filtersToSend = {};
      filters.selectedFilters.forEach((sf1) => {
        filtersToSend[sf1.key] = filters.selectedFilters
          .filter((sf2) => sf1.key === sf2.key)
          .map((sf2) => sf2.values)[0];
      });
      booksRequest(
        {
          filters: { ...filtersToSend },
          search_key: searchKey.current || keyword,
          section_type: tab.current,
        },
        `?page=${page}`
      );
      // in there it a search key call magazines editions
      if (searchKey.current || keyword)
        magazinesRequest(
          {
            filters: { ...filtersToSend },
            search_key: searchKey.current || keyword,
            section_type: tab.current,
          },
          `?page=${page}`
        );
    },
    [booksRequest, filters.selectedFilters, page, searchKey, magazinesRequest]
  );

  React.useEffect(() => {
    handleGetData();
  }, [handleGetData]); // get data from the API

  React.useEffect(() => {
    booksFilterRequest({
      page_type: "books",
      parent_ids: [],
      section_ids: [3],
    });
  }, [booksFilterRequest]); // call Research And Studies filters

  return (
    <Layout>
      <div id="magazines_page" className="page_container pt-5">
        <section className="mb-3 page_top">
          <Container>
            <Row>
              <Col xs={12} className="mb-4">
                <h1 className="fw-medium text-primary text-center">
                  {language.magazinesSection}
                </h1>
              </Col>
              <Col xs={12} className="mb-4">
                <TabSlider
                  tabs={Tabs}
                  currentTab={currentTab}
                  setCurrentTab={handleSetTab}
                  alternate
                />
                {/* component that take tabs as a array and show it and handel tabs active */}
              </Col>
              <Col xs={12}>
                <Row className="align-items-center">
                  <Col xl={2} lg={2} md={0}></Col>
                  <Col xl={8} lg={8} md={12}>
                    <Row className="align-items-center">
                      <Col xl={9} lg={9} xs={9}>
                        <SearchForm onSubmit={handleSearch} />
                      </Col>
                      <Col
                        xl={3}
                        lg={3}
                        md={3}
                        sm={3}
                        xs={3}
                        className="px-1 px-sm-2"
                      >
                        <NavLink
                          to="/search/advanced"
                          className="btn btn-primary btn-outline-primary-hover w-100 px-1 px-sm-3"
                        >
                          {language.advanceSearch}
                        </NavLink>
                      </Col>
                    </Row>
                  </Col>
                  <Col xl={2} lg={2} md={0}></Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="mb-3">
          <Container>
            <Row>
              {tab.current === 3 && (
                <Col xl={3} lg={3} md={12} sm={12} xs={12}>
                  {!booksFilterResponse.loading &&
                    booksFilterResponse.result.data && (
                      <SideFilter
                        title={language.booksFilter}
                        filters={filters}
                        type="checkbox"
                      />
                      // component that displays filters and handel its actions by using useFilter
                    )}
                </Col>
              )}
              {searchKey.current && tab.current === 2 ? (
                <Col
                  lg={tab.current === 3 ? 9 : 12}
                  xs={12}
                  className="mx-auto"
                >
                  {!magazinesResponse.loading &&
                  magazinesResponse.result.data ? (
                    <GridList
                      perRow={4}
                      itemsCount={magazinesResponse.result.meta.total}
                      perPage={magazinesResponse.result.meta.per_page}
                      as={MagazineLinkCard}
                      items={magazinesResponse.result.data}
                    /> // magazine search result ( editions )
                  ) : (
                    <Skeletons.CardsSkeleton count={12} perRow={4} />
                  )}
                </Col>
              ) : (
                <Col
                  lg={tab.current === 3 ? 9 : 12}
                  xs={12}
                  className="mx-auto"
                >
                  {!booksResponse.loading && booksResponse.result.data ? (
                    <GridList
                      perRow={tab.current === 3 ? 3 : 4}
                      itemsCount={booksResponse.result.meta.total}
                      perPage={booksResponse.result.meta.per_page}
                      as={MultimediaCard}
                      items={booksResponse.result.data}
                    /> // if the tab 3 that mean there is a filter side that why perRow in tab 3 equal 3 to show in xl 3 card in each row
                  ) : (
                    <Skeletons.CardsSkeleton
                      count={12}
                      perRow={tab.current === 3 ? 3 : 4}
                    />
                  )}
                </Col>
              )}
            </Row>
          </Container>
        </section>
      </div>
    </Layout>
  );
}
